<template>
	<nav
		ref="navElement"
		class="entry-view-tabs flex items-center grow max-w-full min-w-0"
		:class="[
			'entry-view-tabs--' + viewType.toLowerCase(),
			{'entry-view-tabs--dropdown': isSingleDropdown },
			{'opacity-50': isResizing && views.length > 1 && !isSingleDropdown }
		]"
	>
		<!-- @vue-ignore ref -->
		<CTabs
			v-if="!isSingleDropdown"
			ref="tabViews"
			:value="selectedViewId"
			:tabs="visibleItems"
			:can-edit-views="canEditViews"
			:has-trigger="canEditViews"
			class="min-w-0 p-0 shrink-0"
			tabindex="-1"
			@input="changeView"
			@open-context-menu="openContextMenu"
		>
			<template v-if="canEditViews" #context-menu="tab">
				<CViewSettingsContextMenuContainer
					v-if="!tab.isLocked"
					:view-id="tab.value"
					:workflow-links-enabled="formData.WorkflowLinksEnabled"
					:email-role-assignments="formData.EmailRoleAssignments"
					:is-multi-page-form="formData.IsMultiPageForm"
					:roles="formData.Roles"
					:is-org-owner="isOrgOwner"
					is-in-tabs
					@save-view="$emit('save-view', $event)"
					@delete-view="deleteView(tab.value)"
					@view-limit-exceeded="showEntryLimitExceededDialog = true"
					@menu-opened="handleMenuOpened"
				/>
			</template>
		</CTabs>
		<!-- @vue-ignore ref -->
		<CEntryViewsMenu
			v-if="overflowItemCount || isSingleDropdown"
			ref="dropdown"
			:view-id="viewId"
			:view-name="viewName"
			:view-type="viewType"
			:is-task-view="isTaskView"
			:entries-count="currentViewEntryCount"
			:can-edit-views="canEditViews"
			:show-workflow-tasks="showWorkflowTasks"
			:views="views"
			:use-mobile-layout="useMobileLayout"
			:form-name="formName"
			class="min-w-0"
			:workflow-links-enabled="formData.WorkflowLinksEnabled"
			:email-role-assignments="formData.EmailRoleAssignments"
			:is-multi-page-form="formData.IsMultiPageForm"
			:roles="formData.Roles"
			:is-org-owner="isOrgOwner"
			:form-id="formId"
			:global-store="globalStore"
			@change-view="$emit('change-view',$event)"
			@new-view="handleNewView"
			@reorder-view="$emit('reorder-view', $event)"
			@save-view="$emit('save-view', $event)"
			@delete-view="deleteView"
			@view-limit-exceeded="showEntryLimitExceededDialog = true"
		>
			<template v-if="!useMobileLayout" #title>
				<div v-if="isSingleDropdown" class="text-button min-w-0 w-full">
					<div class="entry-view-tabs__dropdown-icon flex items-center justify-center">
						<Inline :svg="selectedView.icon" />
					</div>
					<span class="truncate font-bold">{{ selectedView.name }}</span>
					<CBadge
						v-if="selectedView.badge"
						:text="selectedView.badge.text"
						:icon="selectedView.badge.icon"
						:type="selectedView.badge.type"
						:is-loading="selectedView.badge.isLoading"
					/>
					<span v-if="selectedView.suffix" class="entry-view-tabs__dropdown-suffix">{{ selectedView.suffix }}</span>
					<div class="entry-view-tabs__dropdown-icon flex items-center justify-center">
						<Inline svg="chevron-down" />
					</div>
				</div>
				<div v-else class="entry-view-tabs__more-menu-button whitespace-nowrap">
					<span class="inline-block entry-view-tabs__more-menu-label">{{ overflowItemCount }} more</span>
					<Inline svg="chevron-down" />
				</div>
			</template>
		</CEntryViewsMenu>
		<CNewView
			v-if="canEditViews && !useMobileLayout"
			class="ml-3"
			@new-view="handleNewView"
		/>
		<CEntryViewLimitExceededDialog
			:show="showEntryLimitExceededDialog"
			:limit="globalStore.organization.Plan.EntryViewLimit"
			@close="showEntryLimitExceededDialog = false"
		/>

		<CUpsellDialog
			:show="showTasksUpsell"
			:org-code="globalStore.organization.Code"
			:is-org-owner="isOrgOwner"
			feature="tasks"
			@close="showTasksUpsell = false"
		/>
	</nav>
</template>

<script lang="ts">
	import { defineComponent, ref, computed, watch, onBeforeUnmount } from 'vue';
	import { useOverflowTabs } from 'src/composables/use-overflow-tabs';
	import CTabs from 'src/components/form-controls/Tabs.vue';
	import CBadge from 'src/components/info/Badge.vue';
	import CNewView from 'src/views/entries/NewView.vue';
	import CEntryViewsMenu from 'src/views/entries/EntryViewsMenu.vue';
	import CEntryViewLimitExceededDialog from 'src/components/dialogs/EntryViewLimitExceededDialog.vue';
	import CUpsellDialog from 'src/components/dialogs/UpsellDialog.vue';
	import CViewSettingsContextMenuContainer from 'src/components/navigation/context-menu/ViewSettingsContextMenuContainer.vue';
	import { useContextMenu } from 'src/components/navigation/context-menu/use-context-menu';
	import { Sortable } from '@shopify/draggable';
	import type { PropType } from 'vue';
	import type { GlobalStore } from 'src/stores/global-store';
	import type { MenuViewData } from 'src/views/entries/EntryViewsMenu.vue';
	import type { EntryViewsFormData } from '@cognitoforms/types/server-types/forms/model/entry-views-form-data';
	import type { BadgeProps } from 'src/components/info/Badge.vue';

	export type EntryViewTab = {
		name: string;
		value: string;
		icon: string;
		badge?: BadgeProps;
		suffix?: number;
		isLocked?: boolean;
	}

	export default defineComponent({
		name: 'CEntryViewTabs',
		components: {
			CTabs,
			CNewView,
			CViewSettingsContextMenuContainer,
			CEntryViewsMenu,
			CBadge,
			CEntryViewLimitExceededDialog,
			CUpsellDialog
		},
		props: {
			tabs: {
				type: Array as PropType<EntryViewTab[]>,
				required: true
			},
			selectedViewId: {
				type: String,
				required: true
			},
			globalStore: {
				type: Object as PropType<GlobalStore>,
				required: true
			},
			viewId: {
				type: String,
				required: true
			},
			views: {
				type: Array as PropType<MenuViewData[]>,
				required: true
			},
			canEditViews: {
				type: Boolean,
				default: false
			},
			currentViewEntryCount: {
				type: Number as PropType<number | null>,
				default: 0
			},
			viewName: {
				type: String,
				required: true
			},
			viewType: {
				type: String,
				required: true
			},
			isTaskView: {
				type: Boolean,
				default: false
			},
			showWorkflowTasks: {
				type: Boolean,
				default: false
			},
			useMobileLayout: {
				type: Boolean,
				default: false
			},
			isOrgOwner: {
				type: Boolean,
				default: false
			},
			formData: {
				type: Object as PropType<EntryViewsFormData>,
				required: true
			},
			formName: {
				type: String,
				required: true
			},
			formId: {
				type: String,
				default: null
			}
		},
		emits: ['change-view', 'new-view', 'reorder-view', 'save-view', 'delete-view'],
		setup(props, { emit }) {
			const dropdown = ref<InstanceType<typeof CEntryViewsMenu> | null>(null);
			const navElement = ref<HTMLElement | null>(null);
			const viewContextMenuRef = ref(null);

			const contextMenu = ref(null);

			const selectedView = computed(() => {
				return props.tabs.find(view => view.value === props.selectedViewId);
			});

			const tabViews = ref<InstanceType<typeof CTabs> | null>(null);
			let visualTabOrder = [];

			let sortableInstance = null;
			const dragging = ref(false);
			const sortableInitialized = ref(false);

			const destroyDraggable = () => {
				sortableInstance?.destroy();
				sortableInstance = null;
				sortableInitialized.value = false;
			};

			const toggleSortableTabs = () => {
				if (!props.canEditViews || props.tabs.length <= 1) {
					destroyDraggable();
					return;
				}

				const container = tabViews.value?.$el as HTMLElement;
				if (!container)
					return;

				sortableInstance = new Sortable(container, {
					draggable: '.tabs__tab',
					mirror: {
						constrainDimensions: true,
						yAxis: false,
						appendTo: 'body'
					},
					distance: 10
				});

				sortableInitialized.value = true;
				const cancelNonCustomEvents = (e) => {
					// cancel original events
					// they can cause the element under the mirror to move back and forth under it
					if ((!e.dragEvent && !e.isCustom) || (e.dragEvent && !e.dragEvent.isCustom))
						e.cancel();
				};
				sortableInstance.on('drag:over', cancelNonCustomEvents);
				sortableInstance.on('sortable:sorted', cancelNonCustomEvents);
				sortableInstance.on('sortable:sort', cancelNonCustomEvents);
				let prevOverId;
				sortableInstance.on('mirror:move', (evt) => {
					// prevent recursion
					if (evt.isCustom || evt.dragEvent.isCustom) {
						evt.cancel();
						return;
					}
					requestAnimationFrame(() => {
						const mirror = evt.mirror;
						const rect = mirror.getBoundingClientRect();
						const centerX = rect.left + parseInt(rect.width) / 2;
						const centerY = rect.top + parseInt(rect.height) / 2;
						const elementUnderMouse = document.elementFromPoint(centerX, centerY);
						const over = elementUnderMouse.closest('.tabs__tab:not(.draggable-source--is-dragging, [style*="pointer-events"])');
						// We are not over a sortable element
						if (!over) {
							prevOverId = null;
							return;
						}

						const overRect = over.getBoundingClientRect();
						const overCenterX = overRect.left + overRect.width / 2;
						// Prevent undoing sort if it crosses during transition and prematurely moving for smaller length tabs
						// If we have already moved a tab underneath us wait until we have moved away from it before moving it again.
						const overId = over.getAttribute('data-tab-id');
						if (Math.abs(overCenterX - centerX) > 5 && rect.width < overRect.width || overId === prevOverId)
							return;

						prevOverId = overId;

						const dragEvent = {
							source: document.querySelector('.draggable-source--is-dragging'),
							over,
							container,
							overContainer: container,
							sourceContainer: container,
							type: 'drag:over',
							isCustom: true
						};
						sortableInstance.trigger(dragEvent);
					});
				});
				sortableInstance.on('sortable:start', () => {
					dragging.value = true; // used in case dragging triggers longpress
					if (contextMenu.value?.isVisible)
						contextMenu.value.close();
					if (viewContextMenuRef.value) { // handles closing the context menu when opened with the trigger
						viewContextMenuRef.value.closeMenus(`flyout-menu-${viewContextMenuRef.value.entityId}`, null);
					}
				});
				sortableInstance.on('sortable:stop', ({ oldIndex, newIndex }) => {
					dragging.value = false;
					if (oldIndex === newIndex)
						return;
					const removed = visualTabOrder.splice(oldIndex, 1);
					visualTabOrder = visualTabOrder.toSpliced(newIndex, 0, ...removed);
					emit('reorder-view', {
						viewId: visualTabOrder[newIndex],
						afterViewId: visualTabOrder[newIndex - 1]
					});
				});
			};

			watch(() => props.canEditViews, toggleSortableTabs, { immediate: true });
			watch(tabViews, toggleSortableTabs);

			const {
				visibleTabs: visibleItems,
				overflowTabs,
				isSingleDropdown,
				isResizing
			} = useOverflowTabs({
				containerEl: navElement,
				tabs: computed(() => props.tabs),
				selectedTabId: computed(() => props.selectedViewId),
				reservedWidth: 137, // newViewButtonWidth(37) + 100 buffer
				moreButtonReservedWidth: 81,
				singleDropdown: true,
				onBeforeCalculate: () => {
					if (props.useMobileLayout) {
						isSingleDropdown.value = true;
						toggleSortableTabs();
						return false;
					}
				},
				onAfterCalculate: ({ transitionedToSingleDropdown }) => {
					visualTabOrder = visibleItems.value.map(t => t.value);
					if (transitionedToSingleDropdown)
						toggleSortableTabs();
				}
			});

			const overflowItemCount = computed(() => overflowTabs.value.length);

			watch(() => props.tabs.length, () => {
				if (props.tabs.length > 1) {
					if (!sortableInitialized.value)
						toggleSortableTabs();
				}
				else
					destroyDraggable();
			});

			onBeforeUnmount(() => {
				if (sortableInstance)
					sortableInstance.destroy();
			});

			const showEntryLimitExceededDialog = ref(false);
			const showTasksUpsell = ref(false);

			const handleMenuOpened = (menuRef) => {
				viewContextMenuRef.value = menuRef;
			};

			const handleNewView = (type) => {
				if (type === 'Task' && !props.showWorkflowTasks) {
					showTasksUpsell.value = true;
					return;
				}
				// Ensure view limit has not been exceeded
				else if (props.views.length >= props.globalStore.organization.Plan.EntryViewLimit) {
					showEntryLimitExceededDialog.value = true;
					return;
				}

				emit('new-view', type);
			};

			const changeView = (viewId: string) => {
				emit('change-view', viewId);
			};

			const deleteView = (viewId) => {
				emit('delete-view', viewId);
			};

			const openContextMenu = (event: MouseEvent | CustomEvent, tab: string) => {
				if (dragging.value)
					return;
				if (contextMenu.value?.isVisible) {
					contextMenu.value.close();
				}
				contextMenu.value = useContextMenu(tab, 'view', 'viewTabs', 'viewTabs');
				if (event.type === 'contextmenu') {
					const e = event as MouseEvent;
					contextMenu.value.openAt({ x: e.clientX, y: e.clientY }, 'rightClick');
				}
				else if (event.type === 'long-press-callback') {
					const e = event as CustomEvent<PointerEvent>;
					contextMenu.value.openAt({ x: e.detail.clientX, y: e.detail.clientY }, 'longPress');
				}
			};

			return {
				visibleItems,
				overflowItemCount,
				selectedView,
				isSingleDropdown,
				isResizing,
				dropdown,
				navElement,
				tabViews,
				changeView,
				handleNewView,
				deleteView,
				showEntryLimitExceededDialog,
				showTasksUpsell,
				openContextMenu,
				handleMenuOpened
			};
		}
	});
</script>

<style lang="scss">
.entry-view-tabs {
	transition: opacity .1s;

	&:not(.entry-view-tabs--dropdown) {
		align-self: flex-end;

		.entry-views-menu {
			flex-shrink: 0;
			margin-bottom: -$space__1;
			margin-left: $space__3;
		}
	}

	&.entry-view-tabs--dropdown {
		margin-left: $space__2;

		.text-button {
			padding-right: $space__2;
			padding-left: $space__2;
		}
	}

	.tabs {
		gap: $space__4;

		&__tab {
			gap: $space__3 - 1px;

			&:first-child:last-child {
				width: 100%;
			}
		}

		@media (hover: hover) {

			&__tab--editable:not(.tabs__tab--locked):hover {

				.badge {
					opacity: 0;
				}
			}
		}

		&__tab--editable.tabs__tab--form-view {
			position: relative;
			overflow: hidden;

			.tabs__tab-icon,
			.tabs__tab-name,
			.tabs__tab-badge {
				transition: transform .2s, opacity .2s;
			}

			.tabs__tab-badge {
				position: absolute;
				left: 100%;
				opacity: 0;
			}

			@media (hover: hover) {

				&:hover {

					.tabs__tab-icon,
					.tabs__tab-name,
					.tabs__tab-badge {
						transform: translateX(-1 * ($size__icon + $space__3));
					}

					.tabs__tab-icon {
						opacity: 0;
					}

					.tabs__tab-badge {
						opacity: 1;
					}
				}
			}

			.el-dropdown {
				display: block !important;
			}
		}

		&__tab {
			flex-shrink: 0;

			&:not(.is-active) {

				.i-button--subtle:hover {
					background-color: var(--bg-primary);
				}

				&:hover {

					.badge--alt {
						box-shadow: 0 0 0 1px var(--border-primary);
					}
				}
			}

			&--locked {
				color: var(--text-disabled);
			}

			&-badge {
				position: relative;
				min-width: 19px;
				min-height: 19px;

				.el-menu {
					position: absolute;
					left: 50%;
					top: -2px;
					transform: translateX(-50%);
				}
			}

			&:not(:hover) .tabs__tab-badge {

				.el-menu {
					display: none;
				}
			}

			@media (hover: none) {

				.el-menu {
					display: none;
				}
			}

			.i-button--subtle {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 24px;
				height: 24px;
				padding: 0;

				svg {
					color: var(--icon-subtle) !important;
				}
			}
		}

		&__tab.draggable-source--is-dragging {
			opacity: 0;
		}

		&__tab,
		&__tab.is-active {
			min-width: 0;
		}

		&__suffix {
			font-size: $size__1;
		}
	}

	&__right {
		min-width: 100px;
	}

	/* stylelint-disable-next-line scss/at-rule-no-unknown */
	@container resizable-content (max-width: 475px) {

		.text-button {
			position: relative;
			background: var(--shade-3);
			margin-left: 4px;

			.entry-view-tabs__dropdown-icon:last-child {
				display: none !important;
			}

			@media (hover: hover) {

				&:hover {

					.entry-view-tabs__dropdown-icon:last-child {
						display: block !important;
						position: absolute;
						right: $space__3;
					}

					.badge {
						opacity: 0;
					}
				}
			}
		}
	}

	&__dropdown {

		&-icon {
			min-width: $size__icon;
		}

		&-suffix {
			font-size: $size__1;
		}

		/* stylelint-disable-next-line scss/at-rule-no-unknown */
		@container resizable-content (max-width: 475px) {

			.tabs__tab {

				.c-icon:not(.i-chevron-down),
				span:not([class]) {
					display: none;
				}
			}
		}
	}

	&__more-menu-button {
		padding: 10px $space__3 12px;

		@include hover;
	}
}

// draggable-mirror is appended to the body
.draggable-mirror.tabs__tab {
	background: var(--bg-primary);

	.view-settings-context-menu-container {
		visibility: hidden;
	}
}
</style>